import { Sensor, SensorType } from "common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input, Label, Text, XStack, YStack } from "tamagui";

import { Select } from "../../components/Select";

// Maps a sensor type to its GBL bucket SKU folder (`{sku}/{version}/image.gbl`).
// This map is ALSO the OTA-eligibility gate: a type with no entry is not
// OTA-eligible and the Update button stays hidden/disabled. SKU strings match
// each variant's tag prefix in home_guardian_end_device (`ld` is renamed `leak`
// here because the S3 bucket layout predates the per-variant tag convention).
//
// Leak detector OTA validated end-to-end 2026-05-28; the other five variants
// never had the leak's self-quiet stall (the SetStartCallback override only
// landed on the leak branch), so they're firmware-code-ready out of the box.
// Bench remediation (canonical bootloader + vendor MFG tokens) is still
// required per device — see project_fleet_sensor_ota_rollout_2026_05_28.md.
export const SENSOR_OTA_SKU: Partial<Record<SensorType, string>> = {
  [SensorType.LEAK_DETECTOR]: "leak",
  [SensorType.AIR_QUALITY]: "aq",
  [SensorType.WATER_QUALITY]: "wq",
  [SensorType.POWER]: "pm",
  [SensorType.HVAC_CONDENSER_MONITOR]: "hvacc",
  [SensorType.HVAC_AIRHANDLER_MONITOR]: "hvacah",
};

export const getSensorOtaSku = (sensor: Sensor): string | null =>
  SENSOR_OTA_SKU[sensor.type as SensorType] ?? null;

// Accepts "1", "1.6", "1.6.2" — the manifest Version must be a clean dotted
// number so image-provider can compare it against the device's CurrentVersion.
const VERSION_PATTERN = /^\d+(\.\d+){0,2}$/;

interface SensorFirmwareUpdateDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  sensor: Sensor | null;
  // Versions with an image.gbl published for this sensor's SKU, newest first
  // (GET /latest-versions lists the bucket). Empty when the list isn't
  // available, in which case the dialog falls back to a free-text box.
  availableVersions?: string[];
  // Marker-published "latest" for the SKU — preselected when it is in the list.
  latestVersion?: string | null;
  onConfirm: (version: string, sku: string) => Promise<void> | void;
}

export function SensorFirmwareUpdateDialog({
  open,
  onOpenChange,
  sensor,
  availableVersions = [],
  latestVersion = null,
  onConfirm,
}: SensorFirmwareUpdateDialogProps) {
  const { t } = useTranslation();
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(false);

  const hasVersionList = availableVersions.length > 0;
  // Stable identity for the effect below: the parent hands us a fresh array
  // every render, but only its contents matter.
  const versionListKey = availableVersions.join("|");

  // Reset the target whenever a new sensor is picked: the published latest
  // when it is really in the bucket, else the newest image present; blank
  // on the manual-entry fallback.
  useEffect(() => {
    if (!open) return;
    if (!hasVersionList) {
      setVersion("");
      return;
    }
    setVersion(
      latestVersion && availableVersions.includes(latestVersion)
        ? latestVersion
        : availableVersions[0],
    );
  }, [open, sensor?.id, hasVersionList, latestVersion, versionListKey]);

  const sku = sensor ? getSensorOtaSku(sensor) : null;
  const trimmed = version.trim();
  const isValid = VERSION_PATTERN.test(trimmed);

  // Tag the marker's latest and the sensor's reported version in the list so
  // the operator can see both at a glance. Neither is blocked: the cloud's
  // idea of the running version can lag a bench reflash, and re-offering the
  // same version is harmless (the hub does nothing when target == current).
  const options = availableVersions.map((candidate) => {
    const tags = [
      candidate === latestVersion ? t("sensors.updateFirmware.latestTag") : null,
      candidate === sensor?.version
        ? t("sensors.updateFirmware.currentTag")
        : null,
    ].filter(Boolean);
    return {
      value: candidate,
      label: tags.length > 0 ? `${candidate} (${tags.join(", ")})` : candidate,
    };
  });

  const onConfirmPress = async () => {
    if (!sku || !isValid) return;
    setLoading(true);
    try {
      await onConfirm(trimmed, sku);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={480}
          maxWidth="95vw"
          maxHeight="90dvh"
          className="scroll-y-auto"
          gap="$4"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          <Dialog.Title
            size="$15"
            fontSize="$11"
            fontWeight="bold"
            $xs={{ fontSize: "$9", lineHeight: "$9" }}
          >
            {t("sensors.updateFirmware.title")}
          </Dialog.Title>

          <Dialog.Description fontSize="$3" color="$gray11">
            {t("sensors.updateFirmware.description", {
              name: sensor?.name || sensor?.serialNumber || "",
            })}
          </Dialog.Description>

          <YStack gap="$2">
            <XStack justifyContent="space-between">
              <Text color="$gray11">{t("sensors.updateFirmware.sku")}</Text>
              <Text>{sku ?? "—"}</Text>
            </XStack>
            <XStack justifyContent="space-between">
              <Text color="$gray11">
                {t("sensors.updateFirmware.currentVersion")}
              </Text>
              <Text>{sensor?.version || "—"}</Text>
            </XStack>
          </YStack>

          <YStack gap="$2">
            <Label htmlFor="ota-version">
              {t("sensors.updateFirmware.targetVersion")}
            </Label>
            {hasVersionList ? (
              <Select
                id="ota-version"
                value={version}
                onValueChange={setVersion}
                options={options}
                placeholder={t("sensors.updateFirmware.selectVersion")}
              />
            ) : (
              <>
                <Input
                  id="ota-version"
                  value={version}
                  onChangeText={setVersion}
                  placeholder="1.6.2"
                  autoCapitalize="none"
                />
                {trimmed.length > 0 && !isValid && (
                  <Text color="$red10" fontSize="$2">
                    {t("sensors.updateFirmware.invalidVersion")}
                  </Text>
                )}
                <Text color="$gray11" fontSize="$2">
                  {t("sensors.updateFirmware.noVersionList")}
                </Text>
              </>
            )}
          </YStack>

          <XStack gap="$3" justifyContent="flex-end">
            <Button onPress={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              backgroundColor="$hgPrimary"
              disabled={!sku || !isValid || loading}
              disabledStyle={{ opacity: 0.5, cursor: "default" }}
              onPress={onConfirmPress}
            >
              {loading
                ? t("sensors.updateFirmware.sending")
                : t("sensors.updateFirmware.confirm")}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default SensorFirmwareUpdateDialog;
